import React, { useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
import Tooltip from '@/components/ui/Tooltip';
import Popover from '@/components/ui/Popover';
import { findChildByType } from '@/components/ui/ui.utils';
import { twMerge } from '@/utils';
import {
  ButtonProps,
  buttonVariants,
  containerVariants,
  DropdownContentComponent,
  ButtonContentComponent,
} from './Button.utils';

const dropdownButtonVariants = tv({
  base: [
    'tw-flex tw-items-center tw-justify-center',
    'tw-h-full',
    'tw-px-2 tw-mr-0',
    'tw-border-0 tw-rounded-r',
  ],
  variants: {
    disabled: {
      false: 'hover:!tw-bg-light-grey tw-text-tci-purple tw-cursor-pointer',
      true: 'tw-cursor-not-allowed tw-text-grey',
    },
    draggableToolbar: {
      true: '!tw-px-1 hover:!tw-bg-white/30',
    },
  },
});

interface ButtonWithAdjacentDropdownProps extends ButtonProps {
  caretColor?: string;
  containerClassName?: string;
  dropDownAriaLabel?: string;
  isInDraggableToolbar?: boolean;
  onAdjacentButtonClick: (e: any) => void;
  onButtonClick: () => void;
}

const ButtonWithAdjacentDropdown = ({
  ariaLabel,
  caretColor,
  children,
  className,
  disabled = false,
  dropDownAriaLabel,
  dropdownContent,
  isInDraggableToolbar = false,
  onAdjacentButtonClick,
  onButtonClick,
  ...props
}: ButtonWithAdjacentDropdownProps) => {
  const dropdownContentOverride = findChildByType(children, ButtonWithAdjacentDropdown.DropdownContent);
  const buttonContentOverride = findChildByType(children, ButtonWithAdjacentDropdown.ButtonContent);

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // AIDEV-NOTE: Force-close the tooltip when the popover opens — without this the
  // hover-opened tooltip floats over the dropdown until the pointer leaves entirely.
  const handlePopoverOpenChange = (open: boolean) => {
    setPopoverOpen(open);
    if (open) setTooltipOpen(false);
  };

  // AIDEV-NOTE: Radix draws a "grace polygon" from the pointer exit point to the tooltip
  // content edges (disableHoverableContent=false for WCAG 1.4.13). Because these toolbar
  // buttons are individually-mounted React roots (react_rails), there's no shared Provider
  // to coordinate "close A when B opens." The grace polygon can keep a tooltip alive while
  // the pointer slides to an adjacent button, causing two tooltips to overlap. This timer
  // overrides that by force-closing after a short delay when the pointer leaves the trigger.
  const handlePointerEnter = () => {
    clearTimeout(closeTimerRef.current);
  };

  const handlePointerLeave = () => {
    closeTimerRef.current = setTimeout(() => setTooltipOpen(false), 100);
  };

  return (
    <Tooltip.Provider>
      <Tooltip delayDuration={0} open={tooltipOpen} onOpenChange={setTooltipOpen}>
        <Popover open={popoverOpen} onOpenChange={handlePopoverOpenChange}>
          <Tooltip.Trigger asChild>
            <div
              className={containerVariants({
                draggableToolbar: isInDraggableToolbar,
                dropdownButtonLocation: 'outside' })}
              onPointerEnter={handlePointerEnter}
              onPointerLeave={handlePointerLeave}
            >
              {/* AIDEV-NOTE: Empty Popover.Anchor anchors Popover.Content to the container div
                  rather than the caret button (Popover.Trigger), so the dropdown spans both
                  buttons instead of opening flush with the caret only. */}
              <Popover.Anchor />
              <button
                aria-disabled={disabled}
                aria-label={ariaLabel}
                className={buttonVariants({
                  disabled,
                  draggableToolbar: isInDraggableToolbar,
                  dropdownButtonLocation: 'outside',
                })}
                type="button"
                onClick={disabled ? undefined : onButtonClick}
                {...props}
              >
                <ButtonContentComponent {...buttonContentOverride?.props} />
              </button>
              <Popover.Trigger asChild>
                <button
                  aria-disabled={disabled}
                  aria-label={dropDownAriaLabel ?? 'Open dropdown menu'}
                  className={dropdownButtonVariants({ disabled, draggableToolbar: isInDraggableToolbar })}
                  disabled={disabled}
                  type="button"
                  onClick={onAdjacentButtonClick}
                >
                  <i className="fa fa-caret-down" style={{ color: caretColor }} />
                </button>
              </Popover.Trigger>
            </div>
          </Tooltip.Trigger>
          <Popover.Content
            align="start"
            className={twMerge(
              'tw-px-0 tw-my-0.5 tw-w-40',
              dropdownContentOverride?.props?.className,
            )}
            side="bottom"
            sideOffset={0}
            onCloseAutoFocus={e => e.preventDefault()}
          >
            <DropdownContentComponent {...dropdownContentOverride?.props} />
          </Popover.Content>
          <Tooltip.Portal>
            <Tooltip.Content showArrow theme="white">
              {ariaLabel}
            </Tooltip.Content>
          </Tooltip.Portal>
        </Popover>
      </Tooltip>
    </Tooltip.Provider>
  );
};

ButtonWithAdjacentDropdown.DropdownContent = DropdownContentComponent;
ButtonWithAdjacentDropdown.ButtonContent = ButtonContentComponent;
ButtonWithAdjacentDropdown.Close = Popover.Close;

export default ButtonWithAdjacentDropdown;
